import { useId, useState } from 'react';
import { HelpTooltip } from '../../../../shared/components/HelpTooltip';
import { AddDisciplineDialog } from '../../../healthcare-rates/components/AddDisciplineDialog';
import { DisciplineAndRatesPanel } from '../../../healthcare-rates/components/DisciplineAndRatesPanel';
import { DisciplineField } from '../../../healthcare-rates/components/DisciplineCombobox';
import { TERM_HELP } from '../../../healthcare-rates/types';
import { useCompanyIndustry } from '../../../healthcare-rates/useCompanyIndustry';
import { useHealthcareCapabilities } from '../../../healthcare-rates/useHealthcareCapabilities';
import { peopleGateway } from '../../index';
import { employmentTypeLabels, type EmployeeResponse } from '../../types';
import { DepartmentPicker } from '../../components/DepartmentPicker';
import { EmployeePayRatesPanel } from '../../components/EmployeePayRatesPanel';
import { PayrollDeductionsSelector } from '../../components/PayrollDeductionsSelector';

interface Props {
  employee: EmployeeResponse;
  onEmployeeUpdated?: ((updated: EmployeeResponse) => void) | undefined;
  onPayRatesChanged?: (() => void) | undefined;
}

/**
 * Payroll Details (Figma admin screen 17), employer-controlled. Department and Work Type are the employee's own record fields (the same ones Work Details
 * edits), Pay Type and Rate are the effective-dated pay rates (EPN-185), and the deductions are the company's existing deduction codes. Nothing here is
 * employee-editable. Work Type and Pay Type are fixed lists because they change how pay is classified, so neither can be created here.
 * Discipline is the employee's primary discipline (a professional classification such as Registered Nurse): a searchable list of EPN's defaults and the
 * company's own, saved by Employees with the employee. The "Discipline and service rates" panel below holds the full history, other disciplines and
 * the employee's own service rates.
 */
export function PayrollDetailsSection({ employee, onEmployeeUpdated, onPayRatesChanged }: Props) {
  const [departmentId, setDepartmentId] = useState(employee.departmentId ?? '');
  const [workType, setWorkType] = useState(employee.employmentType ? String(employee.employmentType) : '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const uid = useId();
  const caps = useHealthcareCapabilities();
  const { defaultTags: industryTags, label: industryLabel, isHealthcare } = useCompanyIndustry();
  const [disciplineId, setDisciplineId] = useState(employee.disciplineId ?? '');
  const [disciplineName, setDisciplineName] = useState(employee.disciplineName ?? '');
  const [addDisciplineOpen, setAddDisciplineOpen] = useState(false);
  const [addDisciplineTyped, setAddDisciplineTyped] = useState('');
  const [panelKey, setPanelKey] = useState(0);

  const unchanged = departmentId === (employee.departmentId ?? '') && workType === (employee.employmentType ? String(employee.employmentType) : '')
    && disciplineId === (employee.disciplineId ?? '');

  /** The panel below changed the disciplines (for example a new primary): read the employee again so this field shows the truth. */
  async function refreshEmployee() {
    try {
      const fresh = await peopleGateway.getEmployee(employee.id);
      setDisciplineId(fresh.disciplineId ?? '');
      setDisciplineName(fresh.disciplineName ?? '');
      onEmployeeUpdated?.(fresh);
    } catch {
      // The panel shows its own state; this field simply keeps what it had.
    }
  }

  async function save() {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      // The server replaces the whole personal record, so every existing value is sent back unchanged.
      const updated = await peopleGateway.updateEmployee(employee.id, {
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        dateOfBirth: employee.dateOfBirth,
        ssnLast4: employee.ssnLast4,
        departmentId: departmentId || null,
        jobTitleId: departmentId === (employee.departmentId ?? '') ? employee.jobTitleId : null,
        hireDate: employee.hireDate,
        employeeNumber: employee.employeeNumber,
        phone: employee.phone ?? null,
        gender: employee.gender ?? null,
        isNewHire: employee.isNewHire ?? null,
        employmentType: workType ? Number(workType) : null,
        // Only a discipline that was chosen and changed is sent; Employees ends the old primary and appends the new one in the same transaction.
        ...(disciplineId && disciplineId !== (employee.disciplineId ?? '') ? { disciplineId } : {}),
      });
      onEmployeeUpdated?.(updated);
      setPanelKey((n) => n + 1);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save Payroll Details.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="profile-section">
      <h3 className="profile-section-title">Payroll Details</h3>
      <p className="muted">Select one as your primary rate <span aria-hidden="true">*</span></p>

      <div className="profile-field-grid" data-testid="payroll-classification">
        <DepartmentPicker value={departmentId} onChange={(id) => { setDepartmentId(id); setSaved(false); }} required />
        <div>
          <label htmlFor={`${uid}-discipline`}>Worker category</label>{' '}
          <HelpTooltip label="About Worker category" text={TERM_HELP.discipline} />
          <DisciplineField
            a11y={{ id: `${uid}-discipline`, 'data-field': 'disciplineId', 'aria-describedby': `${uid}-discipline-hint`, 'aria-invalid': undefined, 'aria-required': undefined }}
            value={disciplineId}
            valueLabel={disciplineName}
            canAdd={caps.discipline?.canManageDisciplines !== false}
            onSelect={(d) => { setDisciplineId(d.id); setDisciplineName(d.name); setSaved(false); }}
            onAddRequested={(typed) => { setAddDisciplineTyped(typed); setAddDisciplineOpen(true); }}
          />
          <p id={`${uid}-discipline-hint`} className="muted">The primary worker category. To end one or add another, use Worker category and rates below.</p>
        </div>
        <label>
          Work Type <span aria-hidden="true">*</span>
          <select value={workType} onChange={(e) => { setWorkType(e.target.value); setSaved(false); }} required>
            <option value="">Select a work type</option>
            {Object.entries(employmentTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="muted" role="status">Department, worker category and work type saved.</p>}
      <div style={{ marginTop: 8 }}>
        <button type="button" onClick={() => void save()} disabled={saving || unchanged}>
          {saving ? 'Saving…' : 'Save Department, Worker Category and Work Type'}
        </button>
      </div>

      <h4 style={{ marginTop: 20 }}>Pay Type and Rate</h4>
      <EmployeePayRatesPanel employeeId={employee.id} onChanged={onPayRatesChanged} />

      <DisciplineAndRatesPanel employeeId={employee.id} refreshKey={`${panelKey}`} onChanged={() => { void refreshEmployee(); onPayRatesChanged?.(); }} />

      <PayrollDeductionsSelector employeeId={employee.id} />

      {addDisciplineOpen && (
        <AddDisciplineDialog
          initialName={addDisciplineTyped}
          defaultTags={industryTags}
          industryLabel={industryLabel}
          healthcare={isHealthcare}
          onClose={() => setAddDisciplineOpen(false)}
          onAdded={(d) => { setDisciplineId(d.id); setDisciplineName(d.name); setSaved(false); setAddDisciplineOpen(false); }}
        />
      )}
    </section>
  );
}
